import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type OsintAnswerBody,
  type OsintCreateBody,
  type OsintCreateFromEntityBody,
  osintApi,
} from '@/services/api/osint'

// --- Query keys ---

export const osintKeys = {
  all: ['osint'] as const,
  list: (caseId: string) => ['osint', 'list', caseId] as const,
  detail: (caseId: string, id: string) => ['osint', 'detail', caseId, id] as const,
}

// --- Queries ---

export function useOsintInvestigations(caseId: string) {
  return useQuery({
    queryKey: osintKeys.list(caseId),
    queryFn: () => osintApi.list(caseId),
    enabled: !!caseId,
  })
}

/** Single-investigation poll — refetches while its latest run is active so the
 * detail page flips from live progress to the finished report without an SSE
 * dependency (the SSE endpoint exists for a later slice). */
export function useOsintInvestigation(caseId: string, id: string | undefined) {
  return useQuery({
    queryKey: id ? osintKeys.detail(caseId, id) : ['osint', 'detail', 'none'],
    queryFn: () => osintApi.get(caseId, id as string),
    enabled: !!caseId && !!id,
    refetchInterval: query => {
      const status = query.state.data?.latestRun?.status
      return status === 'queued' || status === 'running' ? 2500 : false
    },
  })
}

// --- Mutations ---

export function useCreateOsintInvestigation(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: OsintCreateBody) => osintApi.create(caseId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: osintKeys.list(caseId) })
    },
  })
}

/** Start an investigation for an entity that is already in the case graph. */
export function useCreateOsintInvestigationForEntity(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: OsintCreateFromEntityBody) => osintApi.createFromEntity(caseId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: osintKeys.list(caseId) })
    },
  })
}

/** Answer the agent's pending clarification questions and resume the run. */
export function useAnswerOsintClarification(caseId: string, id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: OsintAnswerBody) => osintApi.answer(caseId, id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: osintKeys.detail(caseId, id) })
    },
  })
}

/** Skip the clarification and finalize with what the agent already has. */
export function useFinalizeOsintNow(caseId: string, id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => osintApi.finalizeNow(caseId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: osintKeys.detail(caseId, id) })
    },
  })
}

/**
 * Write the selected proposed entities into the case graph and get the written
 * subgraph back. The graph itself changed, so the graph/entity caches are
 * dropped — the object-set merge is the caller's job (it needs the returned
 * view).
 */
export function useImportOsintProposal(caseId: string, id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (entityKeys: string[]) => osintApi.importProposal(caseId, id, entityKeys),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['graph'] })
      qc.invalidateQueries({ queryKey: ['case-entities-by-type'] })
    },
  })
}
